import _ from "lodash";

import jsonTemplate from "../data/template";
import * as entityUtils from "./entityUtils";
import { transformFromChecklistEntities, transformToChecklistEntities } from "./transformUtils";
import { convertStringToDriveRootPath, generateUUID } from "./utils";

export const ASSET_FOLDER_STRUCTURE_ITEMS = "items";
export const ASSET_FOLDER_STRUCTURE_SECTIONS = "sections";
export const ASSET_FOLDER_STRUCTURE_LIST_SECTIONS = "listSections";

export const ASSET_FOLDER_STRUCTURE_OPTIONS = [
	{
		value: ASSET_FOLDER_STRUCTURE_ITEMS,
		label: "Activity > Assets",
		description: "Create each selected image as a direct item child."
	},
	{
		value: ASSET_FOLDER_STRUCTURE_SECTIONS,
		label: "Activity > Section > Assets",
		description: "Create one or more sections, then place image items under them."
	},
	{
		value: ASSET_FOLDER_STRUCTURE_LIST_SECTIONS,
		label: "Activity > List > Section > Assets",
		description: "Create a list, one or more sections, then place image items under them."
	}
];

const SUPPORTED_IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "webp", "gif", "bmp"]);

export function isTopLevelFolderFile(file) {
	const relativePath = `${_.get(file, "webkitRelativePath", "")}`.trim();
	if (!relativePath) {
		return true;
	}
	return relativePath.split("/").filter(Boolean).length <= 2;
}

export function getAssetFileExtension(fileOrName = "") {
	const name = _.isString(fileOrName) ? fileOrName : `${_.get(fileOrName, "name", "")}`;
	const parts = name.split(".");
	return parts.length > 1 ? `${parts.pop()}`.toLowerCase() : "";
}

export function getSupportedAssetKind(file = null) {
	const mimeType = `${_.get(file, "type", "")}`.toLowerCase();
	if (mimeType.startsWith("image/")) {
		return "image";
	}

	const ext = getAssetFileExtension(file);
	if (SUPPORTED_IMAGE_EXTENSIONS.has(ext)) {
		return "image";
	}

	return null;
}

export function isSupportedAssetFile(file = null) {
	return !!getSupportedAssetKind(file);
}

export function getAssetFolderNameFromFiles(files = []) {
	const firstFile = Array.isArray(files) ? files.find((file) => file && file.webkitRelativePath) : null;
	const relativePath = firstFile ? `${firstFile.webkitRelativePath || ""}` : "";
	const folderName = relativePath.split("/").filter(Boolean)[0];
	return folderName || "Asset Folder";
}

export function getDefaultAssetLabel(fileName = "", index = 0) {
	const baseName = `${fileName || ""}`.replace(/\.[^/.]+$/, "").trim();
	const slideMatch = baseName.match(/^slide[\s_-]*(\d+)$/i);
	if (slideMatch) {
		return `Slide ${slideMatch[1]}`;
	}

	const normalized = baseName.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
	return normalized || `Asset ${index + 1}`;
}

export function sortAssetEntries(entries = []) {
	return [...(Array.isArray(entries) ? entries : [])].sort((a, b) => {
		const aPath = `${a.relativePath || a.name || ""}`;
		const bPath = `${b.relativePath || b.name || ""}`;
		return aPath.localeCompare(bPath, undefined, {
			numeric: true,
			sensitivity: "base"
		});
	});
}

export function normalizeAssetFolderFiles(files = [], options = {}) {
	const includeSubfolders = options.includeSubfolders !== false;
	return sortAssetEntries(
		(Array.isArray(files) ? files : [])
			.filter((file) => file && file.name)
			.filter((file) => includeSubfolders || isTopLevelFolderFile(file))
			.filter(isSupportedAssetFile)
			.map((file, index) => {
				const relativePath = `${file.webkitRelativePath || file.name || ""}`;
				return {
					id: generateUUID(),
					index,
					file,
					name: file.name,
					relativePath,
					assetKind: getSupportedAssetKind(file),
					label: getDefaultAssetLabel(file.name, index),
					sectionStart: false,
					sectionTitle: ""
				};
			})
	).map((asset, index) => ({
		...asset,
		index,
		sectionStart: index === 0 ? true : asset.sectionStart,
		sectionTitle: index === 0 && !asset.sectionTitle ? (options.defaultSectionName || options.folderName || "Assets") : asset.sectionTitle
	}));
}

export function getAllowedAssetFolderStructureOptions(targetType = "", mode = "activity") {
	if (mode !== "outline") {
		return ASSET_FOLDER_STRUCTURE_OPTIONS;
	}

	const normalizedTargetType = `${targetType || ""}`.trim();
	if (normalizedTargetType === "section" || normalizedTargetType.startsWith("item")) {
		return ASSET_FOLDER_STRUCTURE_OPTIONS.filter((option) => option.value === ASSET_FOLDER_STRUCTURE_ITEMS);
	}

	if (normalizedTargetType === "list") {
		return ASSET_FOLDER_STRUCTURE_OPTIONS.filter((option) => option.value === ASSET_FOLDER_STRUCTURE_SECTIONS);
	}

	return ASSET_FOLDER_STRUCTURE_OPTIONS;
}

export function getEffectiveAssetFolderStructurePreset(targetType = "", requestedPreset = ASSET_FOLDER_STRUCTURE_SECTIONS, mode = "activity") {
	const allowedOptions = getAllowedAssetFolderStructureOptions(targetType, mode);
	const allowedValues = allowedOptions.map((option) => option.value);
	return allowedValues.includes(requestedPreset) ? requestedPreset : _.get(allowedOptions, "[0].value", ASSET_FOLDER_STRUCTURE_ITEMS);
}

export function getAssetFolderTargetDescription(targetType = "") {
	const normalizedTargetType = `${targetType || ""}`.trim();
	if (normalizedTargetType === "section") {
		return "The selected section will receive one item per image.";
	}
	if (normalizedTargetType.startsWith("item")) {
		return "Images will be inserted as sibling items after the selected item.";
	}
	if (normalizedTargetType === "list") {
		return "The selected list will receive section children, with image items under those sections.";
	}
	return "The activity root can receive direct items, sections, or a list/section structure.";
}

export function buildAssetFolderDriveBasePath(label = "asset-folder-import") {
	const slug = convertStringToDriveRootPath(label || "asset-folder-import") || "asset-folder-import";
	return `private/imports/${slug}-${Date.now()}`;
}

export function createAssetFolderResourceMetadata(asset = {}, config = {}) {
	const metadata = {
		type: "image",
		source: "asset-folder-import",
		sourceFileName: asset.name || "",
		sourceRelativePath: asset.relativePath || asset.name || "",
		assetIndex: Number.isFinite(Number(asset.index)) ? Number(asset.index) : 0,
		assetLabel: asset.label || "",
		folderName: config.folderName || "",
		collectionLabel: config.collectionLabel || "",
		fileNamePrefix: config.fileNamePrefix || "",
		sectionTitle: asset.sectionTitle || "",
		importedAt: new Date().toISOString()
	};
	const sourcePresentation = buildSourcePresentationMetadata(config);
	if (sourcePresentation) {
		metadata.sourcePresentation = sourcePresentation;
	}
	const sourcePresentationSlide = buildSourcePresentationSlideMetadata(asset);
	if (sourcePresentationSlide) {
		metadata.sourcePresentationSlide = sourcePresentationSlide;
		metadata.sourcePresentationSlideMatch = asset.sourcePresentationSlideMatch || null;
	}
	return metadata;
}

export function createAssetFolderSourcePresentationMetadata(file = null, manifest = null, config = {}) {
	const deck = manifest && manifest.deck ? manifest.deck : {};
	return {
		type: "presentation",
		source: "asset-folder-import",
		sourceType: "pptx",
		sourceFileName: _.get(file, "name", "") || deck.fileName || "",
		folderName: config.folderName || "",
		collectionLabel: config.collectionLabel || "",
		fileNamePrefix: config.fileNamePrefix || "",
		title: deck.title || "",
		slideCount: deck.slideCount || (manifest && Array.isArray(manifest.slides) ? manifest.slides.length : 0),
		created: deck.created || "",
		modified: deck.modified || "",
		importedAt: new Date().toISOString()
	};
}

function buildSourcePresentationMetadata(config = {}) {
	const manifest = config.sourcePresentationManifest || {};
	const deck = manifest.deck || {};
	const driveObject = config.sourcePresentationDriveObject || {};
	const slideCount = deck.slideCount || (Array.isArray(manifest.slides) ? manifest.slides.length : 0);
	const fileName = deck.fileName || _.get(config, "sourcePresentationFile.name", "") || "";
	const hasSourcePresentation = !!(fileName || slideCount || driveObject.guid || driveObject.path);
	if (!hasSourcePresentation) {
		return null;
	}

	return {
		source: "pptx",
		fileName,
		title: deck.title || "",
		slideCount,
		created: deck.created || "",
		modified: deck.modified || "",
		creator: deck.creator || "",
		lastModifiedBy: deck.lastModifiedBy || "",
		widthEmu: deck.widthEmu || null,
		heightEmu: deck.heightEmu || null,
		widthInches: deck.widthInches || null,
		heightInches: deck.heightInches || null,
		aspectRatio: deck.aspectRatio || null,
		driveObjectGuid: driveObject.guid || "",
		driveObjectPath: driveObject.path || ""
	};
}

function buildSourcePresentationSlideMetadata(asset = {}) {
	const slide = asset.sourcePresentationSlide || null;
	if (!slide) {
		return null;
	}

	return {
		index: slide.index,
		slideId: slide.slideId || "",
		relationshipId: slide.relationshipId || "",
		path: slide.path || "",
		fileName: slide.fileName || "",
		title: slide.title || "",
		text: slide.text || "",
		paragraphs: Array.isArray(slide.paragraphs) ? slide.paragraphs : [],
		notes: slide.notes || "",
		assets: Array.isArray(slide.assets) ? slide.assets : [],
		hyperlinks: Array.isArray(slide.hyperlinks) ? slide.hyperlinks : [],
		shapes: Array.isArray(slide.shapes) ? slide.shapes : []
	};
}

function buildAssetAuthorProperties(asset = {}, config = {}) {
	const authorProperties = {
		assetFolderImport: {
			sourceFileName: asset.name || "",
			sourceRelativePath: asset.relativePath || asset.name || "",
			assetIndex: Number.isFinite(Number(asset.index)) ? Number(asset.index) : 0,
			assetLabel: asset.label || "",
			folderName: config.folderName || "",
			fileNamePrefix: config.fileNamePrefix || "",
			driveObjectGuid: _.get(asset, "driveObject.guid", ""),
			driveObjectPath: _.get(asset, "driveObject.path", "")
		}
	};
	const sourcePresentation = buildSourcePresentationMetadata(config);
	if (sourcePresentation) {
		authorProperties.sourcePresentation = sourcePresentation;
	}
	const sourcePresentationSlide = buildSourcePresentationSlideMetadata(asset);
	if (sourcePresentationSlide) {
		authorProperties.sourcePresentationSlide = sourcePresentationSlide;
		authorProperties.sourcePresentationSlideMatch = asset.sourcePresentationSlideMatch || null;
	}
	return authorProperties;
}

export function buildAssetItemNode(asset = {}, config = {}) {
	const label = `${asset.label || ""}`.trim() || getDefaultAssetLabel(asset.name, Number(asset.index) || 0);
	const labelOnly = config.labelOnly !== false;
	const itemEntity = entityUtils.addItemEntity(generateUUID(), label, "", labelOnly, false, "", "item");
	const createAssetReference = _.isFunction(config.createAssetReference) ? config.createAssetReference : null;
	const resourceReference = createAssetReference ? createAssetReference(asset, config) : asset.resourceReference;

	if (!resourceReference) {
		return null;
	}

	itemEntity.image = resourceReference;
	itemEntity.authorProperties = JSON.stringify(buildAssetAuthorProperties(asset, config), null, 2);
	return entityUtils.addItemNode(itemEntity);
}

function groupAssetsBySections(assets = [], defaultSectionName = "Assets") {
	const groups = [];
	let activeGroup = null;

	assets.forEach((asset, index) => {
		if (!activeGroup || asset.sectionStart === true) {
			const sectionTitle = `${asset.sectionTitle || ""}`.trim();
			activeGroup = {
				label: sectionTitle || (groups.length === 0 ? defaultSectionName : `Section ${groups.length + 1}`),
				assets: []
			};
			groups.push(activeGroup);
		}

		activeGroup.assets.push({
			...asset,
			index
		});
	});

	return groups;
}

export function buildAssetFolderSectionNodes(assets = [], config = {}) {
	const sectionLabel = `${config.sectionLabel || config.folderName || "Assets"}`.trim();
	return groupAssetsBySections(assets, sectionLabel).map((group) => {
		const sectionEntity = entityUtils.addSectionEntity(generateUUID(), group.label);
		const sectionNode = entityUtils.addCategoryNode(sectionEntity, true);
		sectionNode.children = group.assets
			.map((asset) => buildAssetItemNode(asset, config))
			.filter(Boolean);
		return sectionNode;
	}).filter((sectionNode) => sectionNode.children.length > 0);
}

export function buildAssetFolderNodes(config = {}) {
	const assets = sortAssetEntries(config.assets || []).map((asset, index) => ({
		...asset,
		index
	}));
	const structurePreset = config.structurePreset || ASSET_FOLDER_STRUCTURE_SECTIONS;

	if (structurePreset === ASSET_FOLDER_STRUCTURE_ITEMS) {
		return assets
			.map((asset) => buildAssetItemNode(asset, config))
			.filter(Boolean);
	}

	const sectionNodes = buildAssetFolderSectionNodes(assets, config);
	if (structurePreset === ASSET_FOLDER_STRUCTURE_LIST_SECTIONS) {
		const listLabel = `${config.collectionLabel || config.folderName || "Assets"}`.trim();
		const listEntity = entityUtils.addListEntity(generateUUID(), listLabel);
		const listNode = entityUtils.addCategoryNode(listEntity, true);
		listNode.children = sectionNodes;
		return sectionNodes.length > 0 ? [listNode] : [];
	}

	return sectionNodes;
}

export function buildAssetFolderActivity(config = {}) {
	const activityName = `${config.activityName || config.collectionLabel || config.folderName || "Asset Folder Activity"}`.trim();
	const activity = _.cloneDeep(jsonTemplate.template.blank);
	activity.id = generateUUID();
	activity.guid = generateUUID();
	activity.name = activityName;
	activity.driveRootPath = convertStringToDriveRootPath(activityName);
	activity.externalResources = Array.isArray(config.externalResources) ? config.externalResources : [];
	activity.startInShowMode = config.startInShowMode === true;
	activity.allowSwitchToShowMode = config.startInShowMode === true;

	const root = transformToChecklistEntities(activity).root;
	root.children = Array.isArray(config.nodes) ? config.nodes : [];
	root.entity.externalResources = activity.externalResources;
	root.entity.startInShowMode = activity.startInShowMode;
	root.entity.allowSwitchToShowMode = activity.allowSwitchToShowMode;

	return transformFromChecklistEntities(root);
}
